/** PM schedules and plans. */
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";
import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import type { ColumnDef } from "@tanstack/react-table";
import type { ReactElement } from "react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { api, type Schemas } from "@/api/client";
import { unwrap } from "@/api/problem";
import { useAuth } from "@/app/auth";
import { AssetReference } from "@/features/entities/AssetReference";
import { useRebaseSchedule } from "@/features/admin/maintenance/queries";
import { useSites } from "@/features/admin/reference/queries";
import { DataTable } from "@/shared/components/data-table";
import { CompleteDataTable } from "@/shared/components/complete-data-table";
import { downloadTableExport, TableExportButton } from "@/shared/components/table-export-button";
import { TableToolbar, type TableFilterDefinition } from "@/shared/components/table-toolbar";
import { bindTableFilters, tableSortFields } from "@/shared/lib/table-controls";
import { FormDialog } from "@/shared/components/form-dialog";
import { ErrorState, LoadingState, PageHeader, StatusBadge } from "@/shared/components/page";
import { formatDateTime, formatPercent, formatQuantity } from "@/shared/lib/format";
import { PAGE_SIZES, useListState } from "@/shared/lib/list-state";
import { compactParams, type ListState, type PageOf, usePageQuery } from "@/shared/lib/paging";
import { Button } from "@/shared/ui/button";
import { Field, Input } from "@/shared/ui/form";
import { Badge, Tabs, TabsContent, TabsList, TabsTrigger } from "@/shared/ui/primitives";

/** Schedule row. */
export type Schedule = Schemas["ScheduleOut"];
/** Plan row. */
export type Plan = Schemas["PlanOut"];

/** Schedule statuses. */
export const SCHEDULE_STATUSES = ["scheduled", "generated", "overdue", "suspended"] as const;

/** Schedule list filters. */
export interface ScheduleFilters {
  q?: string;
  status?: string;
  plan_id?: string;
  site_id?: string;
  due_before?: string;
  due_after?: string;
}

/** Sorted, numbered page of schedules. */
export function useSchedulePage(filters: ScheduleFilters, state: ListState): PageOf<Schedule> {
  return usePageQuery(["schedules", "page", filters], state, async (params) =>
    unwrap(
      await api.GET("/api/v1/maintenance/schedules", {
        params: { query: compactParams({ ...filters, ...params }) },
      }),
    ),
  );
}

/** Plans. */
export function usePlans(): UseQueryResult<Plan[]> {
  return useQuery({
    queryKey: ["plans"],
    queryFn: async () =>
      unwrap(await api.GET("/api/v1/maintenance/plans", { params: { query: { limit: 200 } } }))
        .items,
  });
}

/** Suspend or resume a schedule. */
export function useToggleSchedule(): UseMutationResult<
  Schedule,
  Error,
  { id: string; action: "suspend" | "resume" }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, action }) =>
      action === "suspend"
        ? unwrap(
            await api.POST("/api/v1/maintenance/schedules/{schedule_id}/suspend", {
              params: { path: { schedule_id: id } },
              body: { reason: "" },
            }),
          )
        : unwrap(
            await api.POST("/api/v1/maintenance/schedules/{schedule_id}/resume", {
              params: { path: { schedule_id: id } },
            }),
          ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["schedules"] });
      void qc.invalidateQueries({ queryKey: ["dashboard"] });
    },
  });
}

interface ScheduleBaseline {
  lastCompletedAt: string;
  lastCompletedMeter: string;
  nextDueAt: string;
  nextDueMeter: string;
}

function dateField(value: string | null): string {
  return value?.slice(0, 10) ?? "";
}

function dateTimePayload(value: string): string | null {
  return value ? `${value}T00:00:00Z` : null;
}

/** Guided baseline editor for last service and the next due point. */
function RebaseDialog({
  schedule,
  onClose,
}: {
  schedule: Schedule;
  onClose: () => void;
}): ReactElement {
  const { t } = useTranslation();
  const rebase = useRebaseSchedule();
  const [values, setValues] = useState<ScheduleBaseline>({
    lastCompletedAt: dateField(schedule.last_completed_at),
    lastCompletedMeter: schedule.last_completed_meter ?? "",
    nextDueAt: dateField(schedule.next_due_at),
    nextDueMeter: schedule.next_due_meter ?? "",
  });
  const [validationError, setValidationError] = useState("");
  const set = (key: keyof ScheduleBaseline, value: string): void =>
    setValues((current) => ({ ...current, [key]: value }));
  const point = (date: string, meter: string): string =>
    [date || "—", meter ? `@ ${meter}` : ""].filter(Boolean).join(" ");
  const submit = async (): Promise<void> => {
    if (!values.nextDueAt && !values.nextDueMeter) {
      setValidationError(t("maintenance.rebaseDueRequired"));
      return;
    }
    setValidationError("");
    await rebase.mutateAsync({
      id: schedule.id,
      body: {
        last_completed_at: dateTimePayload(values.lastCompletedAt),
        last_completed_meter: values.lastCompletedMeter || null,
        next_due_at: dateTimePayload(values.nextDueAt),
        next_due_meter: values.nextDueMeter || null,
      },
    });
    onClose();
  };

  return (
    <FormDialog
      open
      onOpenChange={(open) => !open && onClose()}
      title={t("maintenance.rebaseTitle")}
      onSubmit={submit}
      submitting={rebase.isPending}
      error={validationError || rebase.error?.message}
    >
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Field id="baseline-last-service-date" label={t("maintenance.lastServiceDate")}>
          <Input
            id="baseline-last-service-date"
            type="date"
            value={values.lastCompletedAt}
            onChange={(event) => set("lastCompletedAt", event.target.value)}
          />
        </Field>
        <Field id="baseline-last-service-meter" label={t("maintenance.lastServiceMeter")}>
          <Input
            id="baseline-last-service-meter"
            type="number"
            min="0"
            step="any"
            value={values.lastCompletedMeter}
            onChange={(event) => set("lastCompletedMeter", event.target.value)}
          />
        </Field>
        <Field id="baseline-first-due-date" label={t("maintenance.firstDueDate")}>
          <Input
            id="baseline-first-due-date"
            type="date"
            value={values.nextDueAt}
            onChange={(event) => set("nextDueAt", event.target.value)}
          />
        </Field>
        <Field id="baseline-first-due-meter" label={t("maintenance.firstDueMeter")}>
          <Input
            id="baseline-first-due-meter"
            type="number"
            min="0"
            step="any"
            value={values.nextDueMeter}
            onChange={(event) => set("nextDueMeter", event.target.value)}
          />
        </Field>
      </div>
      <p className="rounded-md bg-muted/40 p-2 text-sm" role="status">
        {t("maintenance.rebasePreview", {
          last: point(values.lastCompletedAt, values.lastCompletedMeter),
          next: point(values.nextDueAt, values.nextDueMeter),
        })}
      </p>
    </FormDialog>
  );
}

/** Schedules table. */
function SchedulesTab(): ReactElement {
  const { t } = useTranslation();
  const { can } = useAuth();
  const list = useListState("/maintenance", "due");
  const { search, state } = list;
  const filters: ScheduleFilters = {
    q: search.q,
    status: search.status,
    plan_id: search.plan_id,
    site_id: search.site_id,
    due_before: search.due_before,
    due_after: search.due_after,
  };
  const page = useSchedulePage(filters, state);
  const plans = usePlans();
  const sites = useSites();
  const toggle = useToggleSchedule();
  const [rebaseSchedule, setRebaseSchedule] = useState<Schedule | null>(null);
  const update = list.update;

  const field = bindTableFilters(filters, update);
  const definitions: TableFilterDefinition[] = [
    {
      ...field("status", t("app.status")),
      kind: "multi",
      prominent: true,
      options: SCHEDULE_STATUSES.map((value) => ({
        value,
        label: t(`maintenance.status.${value}`),
      })),
    },
    {
      ...field("plan_id", t("maintenance.plan")),
      kind: "select",
      prominent: true,
      options: (plans.data ?? []).map((plan) => ({ value: plan.id, label: plan.name })),
    },
    {
      ...field("site_id", t("app.site")),
      kind: "select",
      options: (sites.data ?? []).map((site) => ({ value: site.id, label: site.name })),
    },
    { ...field("due_after", t("tablePilot.dueFrom")), kind: "date", boundary: "start" },
    { ...field("due_before", t("tablePilot.dueUntil")), kind: "date", boundary: "end" },
  ];

  const columns: ColumnDef<Schedule>[] = [
    {
      id: "plan",
      enableSorting: true,
      header: t("maintenance.plan"),
      cell: (c) => <span className="font-medium">{c.row.original.plan_name}</span>,
    },
    {
      id: "asset",
      enableSorting: true,
      header: t("app.asset"),
      cell: (c) => (
        <AssetReference
          id={c.row.original.asset_id}
          code={c.row.original.asset_code}
          name={c.row.original.asset_name}
          context="history"
        />
      ),
    },
    {
      id: "status",
      enableSorting: true,
      header: t("app.status"),
      cell: (c) => <StatusBadge value={c.row.original.status} namespace="maintenance.status" />,
    },
    {
      id: "due",
      enableSorting: true,
      header: t("maintenance.nextDue"),
      cell: (c) => {
        const s = c.row.original;
        const dueAt = s.effective_due_at ?? s.next_due_at;
        return (
          <span className={s.status === "overdue" ? "text-destructive" : undefined}>
            {dueAt ? formatDateTime(dueAt) : s.next_due_meter ? t("maintenance.meterDueOnly") : "—"}
            {s.next_due_meter ? (
              <span className="ms-1 text-xs text-muted-foreground">
                @ {formatQuantity(s.next_due_meter)}
              </span>
            ) : null}
          </span>
        );
      },
    },
    {
      id: "last_completed_at",
      enableSorting: true,
      header: t("maintenance.lastCompleted"),
      cell: (c) => formatDateTime(c.row.original.last_completed_at),
    },
    {
      id: "compliance",
      enableSorting: true,
      header: t("maintenance.compliance"),
      cell: (c) =>
        `${formatPercent(c.row.original.compliance_pct)} (${c.row.original.compliance_count}/${c.row.original.total_count})`,
    },
    {
      id: "wo",
      header: t("maintenance.openWorkOrder"),
      cell: (c) =>
        c.row.original.open_workorder_id ? (
          <Link
            to="/work-orders/$woId"
            params={{ woId: c.row.original.open_workorder_id }}
            className="text-primary hover:underline"
            onClick={(e) => e.stopPropagation()}
          >
            {t("maintenance.openWorkOrder")} →
          </Link>
        ) : (
          "—"
        ),
    },
    ...(can("maintenance.schedule.manage")
      ? [
          {
            id: "actions",
            header: t("app.actions"),
            cell: (c): ReactElement => {
              const s = c.row.original;
              const action = s.status === "suspended" ? "resume" : "suspend";
              return (
                <div className="flex flex-wrap gap-1">
                  <Button
                    size="sm"
                    variant="ghost"
                    disabled={toggle.isPending}
                    onClick={() => toggle.mutate({ id: s.id, action })}
                  >
                    {t(`maintenance.${action}`)}
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => setRebaseSchedule(s)}>
                    {t("maintenance.rebase")}
                  </Button>
                </div>
              );
            },
          } satisfies ColumnDef<Schedule>,
        ]
      : []),
  ];

  return (
    <div>
      {rebaseSchedule ? (
        <RebaseDialog schedule={rebaseSchedule} onClose={() => setRebaseSchedule(null)} />
      ) : null}
      <TableToolbar
        filters={definitions}
        search={search.q}
        searchPlaceholder={t("tablePilot.scheduleSearch")}
        searchDescription={t("tablePilot.scheduleSearchScope")}
        onSearchChange={(q) => update({ q })}
        sorting={state.sort}
        sortFields={tableSortFields(columns)}
        onSortingChange={list.setSort}
        onReset={() =>
          update({
            q: undefined,
            status: undefined,
            plan_id: undefined,
            site_id: undefined,
            due_after: undefined,
            due_before: undefined,
          })
        }
        actions={
          <TableExportButton
            onExport={(format) =>
              downloadTableExport(
                "/api/v1/maintenance/schedules",
                "pm-schedules",
                { ...filters, ordering: state.sort },
                format,
              )
            }
          />
        }
      />
      {page.query.isPending ? (
        <LoadingState />
      ) : page.query.isError ? (
        <ErrorState error={page.query.error} onRetry={() => void page.query.refetch()} />
      ) : (
        <DataTable
          columns={columns}
          data={page.items}
          minWidth={1000}
          getRowId={(s) => s.id}
          sorting={state.sort}
          onSortingChange={list.setSort}
          isFetching={page.query.isFetching}
          pagination={{
            page: page.page,
            pages: page.pages,
            total: page.total,
            pageSize: page.pageSize,
            onPageChange: list.setPage,
            onPageSizeChange: list.setSize,
            sizes: PAGE_SIZES,
            extendedNavigation: true,
          }}
        />
      )}
    </div>
  );
}

/** Plans table. */
function PlansTab(): ReactElement {
  const { t } = useTranslation();
  const q = usePlans();
  const columns: ColumnDef<Plan>[] = [
    {
      id: "code",
      header: t("app.code"),
      cell: (c) => <span className="font-mono text-xs">{c.row.original.code}</span>,
    },
    {
      id: "name",
      header: t("app.name"),
      cell: (c) => <span className="font-medium">{c.row.original.name}</span>,
    },
    {
      id: "template",
      header: t("workOrders.tasks"),
      cell: (c) => c.row.original.job_template_name,
    },
    {
      id: "priority",
      header: t("app.priority"),
      cell: (c) => <StatusBadge value={c.row.original.priority} namespace="workOrders.priority" />,
    },
    {
      id: "triggers",
      header: t("app.type"),
      cell: (c) => (
        <span className="flex flex-wrap gap-1">
          {c.row.original.triggers.map((tr, i) => (
            <Badge key={i} tone="outline">
              {(tr as { kind?: string }).kind ?? "?"}
            </Badge>
          ))}
        </span>
      ),
    },
    {
      id: "schedules",
      header: t("maintenance.schedules"),
      cell: (c) => c.row.original.schedule_count ?? 0,
    },
    {
      id: "active",
      header: t("app.status"),
      cell: (c) =>
        c.row.original.is_active ? (
          <Badge tone="success">{t("app.yes")}</Badge>
        ) : (
          <Badge>{t("app.no")}</Badge>
        ),
    },
  ];
  if (q.isPending) {
    return <LoadingState />;
  }
  if (q.isError) {
    return <ErrorState error={q.error} onRetry={() => void q.refetch()} />;
  }
  return (
    <CompleteDataTable
      columns={columns}
      data={q.data}
      getRowId={(plan) => plan.id}
      getSearchText={(plan) => `${plan.code} ${plan.name}`}
      searchDescription={t("tablePilot.searchFields", {
        fields: `${t("app.code")}, ${t("app.name")}`,
      })}
      sortFields={[
        { value: "code", label: t("app.code"), valueOf: (plan) => plan.code },
        { value: "name", label: t("app.name"), valueOf: (plan) => plan.name },
      ]}
      minWidth={900}
    />
  );
}

/** Maintenance page with schedules and plans tabs. */
export function MaintenancePage(): ReactElement {
  const { t } = useTranslation();
  const search = useSearch({ strict: false });
  const navigate = useNavigate();
  return (
    <div>
      <PageHeader title={t("maintenance.title")} />
      <Tabs
        value={search.tab ?? "schedules"}
        onValueChange={(tab) =>
          void navigate({ to: "/maintenance", search: { tab, size: search.size } })
        }
      >
        <TabsList>
          <TabsTrigger value="schedules">{t("maintenance.schedules")}</TabsTrigger>
          <TabsTrigger value="plans">{t("maintenance.plans")}</TabsTrigger>
        </TabsList>
        <TabsContent value="schedules">
          <SchedulesTab />
        </TabsContent>
        <TabsContent value="plans">
          <PlansTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}
